import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

/**
 * Nava Badge — matches the Figma Badge spec.
 * Axes: color (5 status colors) × variant (primary = subtle fill + border,
 * secondary = text only) × size (sm/md/lg). Renders an inline, non-interactive span.
 */
const badgeVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-full font-medium border',
  {
    variants: {
      color: {
        accent: '',
        neutral: '',
        error: '',
        success: '',
        warning: '',
      },
      variant: {
        primary: '',
        secondary: 'border-transparent bg-transparent',
      },
      size: {
        sm: 'type-text-xs px-space-6 py-space-0',
        md: 'type-text-xs px-space-8 py-space-4',
        lg: 'type-text-sm px-space-8 py-space-4',
      },
    },
    compoundVariants: [
      // Primary: subtle fill + muted border + solid text, per status color.
      {
        variant: 'primary',
        color: 'accent',
        className: 'bg-accent-subtle border-accent-muted text-accent-solid',
      },
      {
        variant: 'primary',
        color: 'neutral',
        className: 'bg-neutral-subtle border-neutral-muted text-neutral-solid',
      },
      {
        variant: 'primary',
        color: 'error',
        className: 'bg-error-subtle border-error-muted text-error-solid',
      },
      {
        variant: 'primary',
        color: 'success',
        className: 'bg-success-subtle border-success-muted text-success-solid',
      },
      {
        variant: 'primary',
        color: 'warning',
        className: 'bg-warning-subtle border-warning-muted text-warning-solid',
      },
      // Secondary: text only (no fill/border), same solid text per color.
      { variant: 'secondary', color: 'accent', className: 'text-accent-solid' },
      {
        variant: 'secondary',
        color: 'neutral',
        className: 'text-neutral-solid',
      },
      { variant: 'secondary', color: 'error', className: 'text-error-solid' },
      {
        variant: 'secondary',
        color: 'success',
        className: 'text-success-solid',
      },
      {
        variant: 'secondary',
        color: 'warning',
        className: 'text-warning-solid',
      },
    ],
    defaultVariants: {
      color: 'neutral',
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface BadgeProps
  extends
    Omit<React.HTMLAttributes<HTMLSpanElement>, 'color'>,
    VariantProps<typeof badgeVariants> {}

/** Inline status label — 5 colors, primary/secondary variants, three sizes. */
export function Badge({ color, variant, size, className, children, ...rest }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ color, variant, size }), className)} {...rest}>
      {children}
    </span>
  );
}

export { badgeVariants };
